/**
 * InvoiceTable — full table card for the invoices list (S11).
 *
 * Structure mirrors `prototype/sales/invoices.html` §"Invoice table":
 *   - .table-card  → paper bg, border, rounded, shadow, overflow-hidden
 *     - .table-wrap → horizontal scroll
 *       - <table>  → thead (bg-raised) + tbody rows
 *     - .table-foot-stats → small KPIs across the visible set
 *     - .table-foot       → "Showing X–Y of N · sorted by …" + pagination
 *
 * Columns: ☐ · # · Customer · Issued · Due · Amount · Status · ⋯
 *
 * Actions per row (kebab):
 *   - View (link to /invoices/<id>)
 *   - Send / Resend (whatsapp)  — disabled when status is draft/void
 *   - Clone (creates a fresh draft, then the parent navigates)
 *   - Void (Founder-only — opens a confirm with mandatory reason)
 *
 * Selection: parent owns the selected-id set; this component just
 * fires `onToggleSelected(id)` and `onToggleSelectAll(checked)`.
 *
 * Pure presentation. No queries. The parent owns data fetching +
 * mutations. Pagination is rendered as a static single page until
 * cursor pagination lands (V1.x).
 *
 * @see prototype/sales/invoices.html — `.table-card`, `.table-foot-stats`, `.table-foot`
 */
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@flint/ui";
import { formatLakhCrore, formatRupees, parsePaise } from "../lib/format-money.ts";

export interface InvoiceTableRow {
  id: string;
  docNumber: string | null;
  customerId: string;
  customerName: string;
  customerGstin?: string | null;
  customerCity?: string | null;
  customerState?: string | null;
  issuedOn: string;
  dueOn: string;
  totalPaise: string;
  /** Cumulative CN total — `'0'` when no credits exist. */
  creditedPaise: string;
  status: "draft" | "issued" | "partially_paid" | "paid" | "overdue" | "void" | "reversed";
}

export interface InvoiceTableProps {
  rows: InvoiceTableRow[];
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  /** When true, "Void" appears in the kebab. */
  isFounder: boolean;
  onSend: (row: InvoiceTableRow) => void;
  onClone: (row: InvoiceTableRow) => void;
  onVoid: (row: InvoiceTableRow) => void;
  /**
   * Open the delete-draft confirm. Only rendered when the row is in
   * `draft` status — issued documents are immutable and go through
   * the void / credit-note path instead.
   */
  onDelete: (row: InvoiceTableRow) => void;
  /**
   * Total invoices matching the current filters (not just the
   * `rows.length` page). Drives the "of N" in the pagination footer.
   * Falls back to `rows.length` when omitted.
   */
  totalCount?: number;
}

export function InvoiceTable({
  rows,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  isFounder,
  onSend,
  onClone,
  onVoid,
  onDelete,
  totalCount,
}: InvoiceTableProps) {
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r.id));

  const visibleCount = rows.length;
  const grandTotal = totalCount ?? visibleCount;
  const stats = computeVisibleStats(rows);

  return (
    <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] table-fixed text-sm">
          <colgroup>
            <col className="w-12" />
            <col className="w-36" />
            <col />
            <col className="w-32" />
            <col className="w-36" />
            <col className="w-32" />
            <col className="w-32" />
            <col className="w-12" />
          </colgroup>
          <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-4">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                />
              </th>
              <th className="px-3 py-4 text-left font-medium">Invoice #</th>
              <th className="px-4 py-4 text-left font-medium">Customer</th>
              <th className="px-3 py-4 text-left font-medium">Issued</th>
              <th className="px-3 py-4 text-left font-medium">Due</th>
              <th className="px-3 py-4 text-right font-medium">Amount</th>
              <th className="px-3 py-4 text-left font-medium">Status</th>
              <th className="px-3 py-4">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = selectedIds.has(row.id);
              const sendable =
                row.status !== "draft" && row.status !== "void" && row.status !== "reversed";
              return (
                <tr
                  key={row.id}
                  className={[
                    "border-t border-line",
                    selected ? "bg-sky-50/50 hover:bg-sky-50" : "hover:bg-raised",
                  ].join(" ")}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.docNumber ?? "draft"}`}
                      checked={selected}
                      onChange={() => onToggleSelected(row.id)}
                    />
                  </td>
                  <td className="px-3 py-3 font-mono font-semibold text-ink-2">
                    {row.status === "draft" ? (
                      // Drafts open the editor directly — most ERPs (Tally,
                      // Zoho, QuickBooks) treat draft-click as "continue
                      // editing", not "view". Issued documents go to the
                      // read-only detail.
                      <Link
                        to="/sales/invoices/$invoiceId/edit"
                        params={{ invoiceId: row.id }}
                        className="block truncate hover:underline"
                      >
                        <span className="text-muted">— draft —</span>
                      </Link>
                    ) : (
                      <Link
                        to="/sales/invoices/$invoiceId"
                        params={{ invoiceId: row.id }}
                        className="block truncate hover:underline"
                      >
                        {row.docNumber}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        aria-hidden
                        className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClass(row.customerName)}`}
                      >
                        {initials(row.customerName)}
                      </span>
                      <div className="min-w-0">
                        <Link
                          to="/sales/customers/$customerId"
                          params={{ customerId: row.customerId }}
                          className="block truncate font-semibold text-ink hover:underline"
                        >
                          {row.customerName}
                        </Link>
                        <p className="truncate text-xs text-muted">{customerMeta(row)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-muted">
                    {formatShortDate(row.issuedOn)}
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-mono text-muted">{formatShortDate(row.dueOn)}</span>
                    <p className={`mt-0.5 text-xs ${dueMetaTone(row.status)}`}>{dueMeta(row)}</p>
                  </td>
                  <td
                    className={[
                      "px-3 py-3 text-right font-mono font-semibold",
                      row.status === "void" || row.status === "reversed"
                        ? "text-muted line-through"
                        : "text-ink",
                    ].join(" ")}
                  >
                    <AmountCell totalPaise={row.totalPaise} creditedPaise={row.creditedPaise} />
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <DropdownMenuRoot>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="p-1 text-muted hover:text-ink-2 rounded"
                          aria-label="Actions"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {row.status === "draft" ? (
                          <DropdownMenuItem asChild>
                            <Link
                              to="/sales/invoices/$invoiceId/edit"
                              params={{ invoiceId: row.id }}
                              className="cursor-pointer flex items-center gap-2"
                            >
                              <Pencil size={14} />
                              <span>Edit draft</span>
                            </Link>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem asChild>
                            <Link
                              to="/sales/invoices/$invoiceId"
                              params={{ invoiceId: row.id }}
                              className="cursor-pointer flex items-center gap-2"
                            >
                              <Eye size={14} />
                              <span>View</span>
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {sendable ? (
                          <DropdownMenuItem
                            onSelect={() => onSend(row)}
                            className="flex items-center gap-2"
                          >
                            <Send size={14} />
                            <span>Send via WhatsApp</span>
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          onSelect={() => onClone(row)}
                          className="flex items-center gap-2"
                        >
                          <Copy size={14} />
                          <span>Clone</span>
                        </DropdownMenuItem>
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => onDelete(row)}
                              className="flex items-center gap-2 text-bad focus:text-bad"
                            >
                              <Trash2 size={14} />
                              <span>Delete draft…</span>
                            </DropdownMenuItem>
                          </>
                        ) : null}
                        {sendable && isFounder ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => onVoid(row)}
                              className="flex items-center gap-2 text-bad focus:text-bad"
                            >
                              <Trash2 size={14} />
                              <span>Void invoice…</span>
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenuRoot>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer 1 — small KPIs across the visible set */}
      {visibleCount > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Visible{" "}
            <strong className="font-mono tabular-nums font-bold text-ink">{visibleCount}</strong>
          </span>
          <span aria-hidden className="h-3 w-px bg-line-2" />
          <span>
            Page total{" "}
            <strong className="font-mono tabular-nums font-bold text-ink">
              {formatLakhCrore(stats.sumPaise)}
            </strong>
          </span>
          <span aria-hidden className="h-3 w-px bg-line-2" />
          <span>
            Avg invoice{" "}
            <strong className="font-mono tabular-nums font-bold text-ink">
              {formatLakhCrore(stats.avgPaise)}
            </strong>
          </span>
          {stats.largest ? (
            <>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Largest{" "}
                <strong className="font-mono tabular-nums font-bold text-ink">
                  {formatLakhCrore(stats.largest.totalPaise)}
                </strong>{" "}
                · {stats.largest.customerName}
              </span>
            </>
          ) : null}
          {stats.dateRange ? (
            <>
              <span aria-hidden className="h-3 w-px bg-line-2" />
              <span>
                Issued{" "}
                <strong className="font-mono tabular-nums font-bold text-ink">
                  {stats.dateRange}
                </strong>
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Footer 2 — pagination + sort caption.
          Cursor pagination lands in V1.x; for now this is a single
          static page that mirrors the prototype's chrome. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
        <span>
          Showing{" "}
          <strong className="font-mono tabular-nums font-bold text-ink">
            {visibleCount === 0 ? "0" : `1–${visibleCount}`}
          </strong>{" "}
          of <strong className="font-mono tabular-nums font-bold text-ink">{grandTotal}</strong> ·
          sorted by issue date (newest first)
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled
            aria-label="Previous page"
            className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft size={12} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            aria-current="page"
            className="grid h-7 min-w-[28px] place-items-center rounded-sm border border-sky-600 bg-sky-600 px-2 text-xs font-semibold text-white"
          >
            1
          </button>
          <button
            type="button"
            disabled
            aria-label="Next page"
            className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronRight size={12} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Visible-set aggregates rendered in the stats footer. Pure
 * derivation from the page rows — no payments data needed, so this
 * stays honest while the receipts vertical is still V1.x.
 */
function computeVisibleStats(rows: InvoiceTableRow[]): {
  sumPaise: bigint;
  avgPaise: bigint;
  largest: { totalPaise: bigint; customerName: string } | null;
  dateRange: string | null;
} {
  if (rows.length === 0) {
    return { sumPaise: 0n, avgPaise: 0n, largest: null, dateRange: null };
  }
  let sum = 0n;
  let largest: { totalPaise: bigint; customerName: string } | null = null;
  let minIssued = rows[0]!.issuedOn;
  let maxIssued = rows[0]!.issuedOn;
  for (const r of rows) {
    // Net of CNs — matches what the Amount column actually displays so
    // the "Page total" footer reconciles with the row stack.
    const net = parsePaise(r.totalPaise) - parsePaise(r.creditedPaise);
    sum += net;
    if (largest === null || net > largest.totalPaise) {
      largest = { totalPaise: net, customerName: r.customerName };
    }
    if (r.issuedOn < minIssued) minIssued = r.issuedOn;
    if (r.issuedOn > maxIssued) maxIssued = r.issuedOn;
  }
  const avg = sum / BigInt(rows.length);
  const dateRange = minIssued === maxIssued ? minIssued : `${minIssued} → ${maxIssued}`;
  return { sumPaise: sum, avgPaise: avg, largest, dateRange };
}

/**
 * Amount cell — when the invoice has no credit notes, renders the
 * total as before. When credits exist, shows the net (total − credited)
 * as the primary number and a struck-through original beneath it so
 * the operator sees the reconciliation without leaving the list.
 */
function AmountCell({ totalPaise, creditedPaise }: { totalPaise: string; creditedPaise: string }) {
  const credited = parsePaise(creditedPaise);
  if (credited === 0n) {
    return <>{formatRupees(totalPaise)}</>;
  }
  const net = parsePaise(totalPaise) - credited;
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{formatRupees(net.toString())}</span>
      <span className="text-xs font-normal text-muted line-through decoration-rose-400">
        {formatRupees(totalPaise)}
      </span>
    </span>
  );
}

function StatusBadge({ status }: { status: InvoiceTableRow["status"] }) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold",
        statusClass(status),
      ].join(" ")}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {statusLabel(status)}
    </span>
  );
}

function statusClass(status: InvoiceTableRow["status"]): string {
  switch (status) {
    case "paid":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "overdue":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "partially_paid":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "issued":
      return "border-sky-200 bg-sky-50 text-sky-700";
    case "void":
      return "border-line bg-bg text-muted";
    case "reversed":
      // Reversed shares the void-style "no receivable" treatment but
      // gets a violet accent so an auditor can tell the two apart at
      // a glance without reading the label.
      return "border-violet-200 bg-violet-50 text-violet-700";
    case "draft":
    default:
      return "border-line bg-raised text-muted";
  }
}

function statusLabel(status: InvoiceTableRow["status"]): string {
  switch (status) {
    case "partially_paid":
      return "Partial";
    case "issued":
      return "Sent";
    case "void":
      return "Voided";
    case "reversed":
      return "Reversed";
    default:
      return status[0]!.toUpperCase() + status.slice(1);
  }
}

function customerMeta(row: InvoiceTableRow): string {
  const parts = [
    row.customerCity ?? row.customerState ?? null,
    row.customerGstin ? "GST" : "No GSTIN",
  ].filter(Boolean);
  return parts.join(" · ");
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).slice(0, 2);
  const value = words.map((word) => word[0]?.toUpperCase() ?? "").join("");
  return value || "IN";
}

function avatarClass(name: string): string {
  const palette = ["bg-sky-600", "bg-rose-600", "bg-emerald-600", "bg-amber-600", "bg-violet-600"];
  const index = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % palette.length;
  return palette[index]!;
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

function dueMeta(row: InvoiceTableRow): string {
  if (row.status === "paid") return "settled";
  if (row.status === "void") return "voided";
  if (row.status === "reversed") return "reversed by CN";
  if (row.status === "draft") return "not issued yet";
  const days = daysFromToday(row.dueOn);
  if (row.status === "overdue" || days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "due today";
  return `due in ${days}d`;
}

function dueMetaTone(status: InvoiceTableRow["status"]): string {
  if (status === "overdue") return "font-semibold text-rose-700";
  if (status === "partially_paid") return "font-semibold text-amber-700";
  return "text-muted";
}

function daysFromToday(iso: string): number {
  const due = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(due.getTime())) return 0;
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - todayUtc) / (1000 * 60 * 60 * 24));
}
